import _ from "lodash";
import { Button, Form, Input, Radio, Select } from "antd";
import { FC, useCallback, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import API from "services/api";
import useCommonStore from "stores/useCommon";

interface Props {
  editionPanelConfig: any;
}

const PanelForm: FC<Props> = ({ editionPanelConfig }) => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const panelForm = Form.useWatch("panel", form);
  const { setIsShowEditionPanel, isShowEditionPanel, ctrlId } =
    useCommonStore();

  const firstConfig = editionPanelConfig?.[0];
  const [visibleQuestionIds, setVisibleQuestionIds] = useState<string[]>([
    firstConfig?.id || "",
  ]);

  const { mutateAsync: mutateUpdateConfigEditionPanel, isPending } =
    useMutation({
      mutationFn: (params: any) => API.mock.updateConfigEditionPanel(params),
    });

  useEffect(() => {
    if (!isShowEditionPanel) {
      form.resetFields();
      if (firstConfig?.id) {
        setVisibleQuestionIds([firstConfig.id]);
      }
    }
  }, [form, isShowEditionPanel, firstConfig?.id]);

  // Update form values when editionPanelConfig changes
  useEffect(() => {
    if (editionPanelConfig && editionPanelConfig.length > 0) {
      form.setFieldsValue({
        panel: editionPanelConfig,
      });
      setVisibleQuestionIds([editionPanelConfig[0].id]);
    }
  }, [editionPanelConfig, form]);

  useEffect(() => {
    if (!panelForm || !firstConfig?.id) {
      return;
    }

    const newVisibleQuestionIds = [firstConfig.id];
    const { newPanelForm, removedIds } = buildNewPanelFormSync(
      panelForm,
      newVisibleQuestionIds
    );

    const newPanel = [...newPanelForm];
    // Pre-set value for questions that are visible
    newPanel.forEach((item: any) => {
      if (
        newVisibleQuestionIds.includes(item.id) &&
        item.value &&
        item.answer === undefined
      ) {
        item.answer = item.value;
      }
    });

    // Remove answers for questions that are no longer visible
    if (!_.isEmpty(removedIds)) {
      removedIds.forEach((id) => {
        const foundedItem = newPanel.find((item: any) => item.id === id);
        delete foundedItem.answer;
      });
    }

    form.setFieldsValue({
      panel: newPanel,
    });

    setVisibleQuestionIds(newVisibleQuestionIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstConfig.id, panelForm]);

  const buildNewPanelFormSync = (
    panel: any,
    newVisibleQuestionIds: string[]
  ) => {
    const newPanelForm = _.sortBy(panel, (q) => {
      const index = _.indexOf(visibleQuestionIds, q.id);
      return index === -1 ? Infinity : index; // Put unlisted items at the end
    });

    newPanelForm?.forEach((item: any) => {
      if (
        item.answer &&
        item.next[item.answer] &&
        item.next[item.answer]?.[0]
      ) {
        newVisibleQuestionIds.push(item.next[item.answer]?.[0]);
      }
    });

    const removedIds = visibleQuestionIds.filter(
      (id) => !newVisibleQuestionIds.includes(id)
    );

    return { newPanelForm, removedIds, newVisibleQuestionIds };
  };

  const handleSubmit = async (dataSubmit: any) => {
    await submit(dataSubmit.panel);
  };

  const handleSubmitChange = async (data: any, index: number) => {
    console.log("🟡 handleSubmitChange called with:", { data, index });
    const newPanel = [...panelForm];
    newPanel[index] = data;
    console.log("🟡 New panel after update:", newPanel);

    const newVisibleQuestionIds = [firstConfig.id];

    const { newPanelForm, removedIds } = buildNewPanelFormSync(
      newPanel,
      newVisibleQuestionIds
    );

    // Pre-set value for questions that are visible
    newPanelForm.forEach((item: any) => {
      if (
        newVisibleQuestionIds.includes(item.id) &&
        item.value &&
        item.answer === undefined
      ) {
        item.answer = item.value;
      }
    });

    // Remove answers for questions that are no longer visible
    if (!_.isEmpty(removedIds)) {
      removedIds.forEach((id) => {
        const foundedItem = newPanelForm.find((item: any) => item.id === id);
        delete foundedItem.answer;
      });
    }

    // Update form values to reflect cleared fields (null -> undefined for visual clearing)
    const formPanel = newPanel.map((item: any) => ({
      ...item,
      answer: item.answer === null ? undefined : item.answer,
    }));
    form.setFieldsValue({
      panel: formPanel,
    });
    console.log("🟡 Form updated with formPanel:", formPanel);

    await submit(newPanel, false);
  };

  const submit = async (dataSubmit: any, isCloseModal: boolean = true) => {
    console.log("🟢 submit called with dataSubmit:", dataSubmit);
    var updateData = dataSubmit
      ?.filter((item: any) => {
        // Include items that have an answer (including empty string and null for clearing)
        const shouldInclude = item.answer !== undefined;
        console.log(`🟢 Filtering item ${item.id}:`, { answer: item.answer, shouldInclude });
        return shouldInclude;
      })
      .map((item: any) => {
        if (item.type === "text") {
          return {
            id: item.id,
            text: item.answer === null ? "" : item.answer,
          };
        }
        // For dropdown/radio, send empty array when cleared (null or empty string)
        return {
          id: item.id,
          selected: (item.answer === "" || item.answer === null) ? [] : [item.answer],
        };
      });

    console.log("🟢 updateData after filtering/mapping:", updateData);

    // Also include items where answer was explicitly deleted (cleared)
    dataSubmit?.forEach((item: any) => {
      const hasAnswerProp = item.hasOwnProperty('answer');
      console.log(`🟢 Checking item ${item.id} for deleted answer:`, {
        hasAnswerProp,
        type: item.type
      });
      if (!hasAnswerProp && item.type !== 'text') {
        console.log(`🟢 Adding cleared item ${item.id} with empty selected array`);
        updateData.push({
          id: item.id,
          selected: [],
        });
      }
    });

    const payload = {
      update: updateData,
      ref_ademe: id,
      ctrl_id: ctrlId,
      action_save: isCloseModal ? "save" : "change",
    };

    console.log("🟢 Final payload being sent:", payload);

    try {
      await mutateUpdateConfigEditionPanel({
        payload
      });

      if (isCloseModal) {
        form.resetFields();
        setIsShowEditionPanel(false);
      }
    } catch (e) {
      console.log("error", e);
    }
  };

  // Handle empty form config
  if (!editionPanelConfig || editionPanelConfig.length === 0) {
    return null;
  }

  return (
    <div className="energy-home__panel-form px-2 sm:px-0">
      <Form
        labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
        wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
        form={form}
        name="panel-form"
        autoComplete="off"
        initialValues={{
          panel: editionPanelConfig,
        }}
        onFinish={handleSubmit}
        className="max-w-xl mx-auto w-full"
      >
        <Form.List name="panel">
          {(fields) => {
            return (
              <div className="flex flex-col">
                {_.sortBy(fields, (field) => {
                  const config = panelForm?.[field.key];
                  const index = _.indexOf(visibleQuestionIds, config?.id);
                  return index === -1 ? Infinity : index; // Put unlisted items at the end
                }).map((field) => {
                  const config = panelForm?.[field.key];
                  if (
                    !visibleQuestionIds.includes(panelForm?.[field.key]?.id)
                  ) {
                    return null;
                  }
                  return (
                    <div key={field.key}>
                      <FormElement
                        config={config}
                        field={field}
                        onSubmit={handleSubmitChange}
                      />
                    </div>
                  );
                })}
              </div>
            );
          }}
        </Form.List>

        <div className="text-right mt-4">
          <Button
            htmlType="submit"
            type="primary"
            loading={isPending}
            style={{ backgroundColor: 'rgb(59, 134, 134)', borderColor: 'rgb(59, 134, 134)' }}
            className="w-full sm:w-auto"
          >
            Envoyer
          </Button>
        </div>
      </Form>
    </div>
  );
};

interface FormElementProps {
  config: any;
  field: any;
  onSubmit?: (data: any, index: number) => void;
}

const FormElement: FC<FormElementProps> = ({ config, field, onSubmit }) => {
  const handleSearchAPI = (text: string) => {
    onSubmit && onSubmit({ ...config, answer: text }, field.name);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleThrottleSearch = useCallback(
    _.throttle(handleSearchAPI, 2000),
    []
  );

  // Remove markdown formatting from label
  const cleanLabel = (text: string) => {
    if (!text) return text;
    return text
      .replace(/\*\*/g, '') // Remove bold markers
      .replace(/\*/g, '')   // Remove italic markers
      .replace(/^#+\s/g, '') // Remove header markers
      .replace(/`/g, '')    // Remove code markers
      .trim();
  };

  if (!config) {
    return null;
  }

  switch (config.type) {
    case "dropdown": {
      const options = config.answers
        ?.filter((item: string) => Boolean(item))
        ?.map((item: string) => {
          return {
            value: item,
            label: item,
          };
        });
      return (
        <Form.Item label={cleanLabel(config?.text)} name={[field.name, "answer"]}>
          <Select
            style={{ maxWidth: '300px' }}
            options={options}
            placement="bottomLeft"
            rootClassName="!z-[10000]"
            allowClear
            onChange={(value) => {
              onSubmit && onSubmit({ ...config, answer: value || "" }, field.name);
            }}
            onClear={() => {
              console.log("🔴 onClear called for field:", field.name, "config:", config);
              // Set answer to null (not undefined) to prevent auto-fill
              const clearedConfig = { ...config, answer: null };
              console.log("🔴 Cleared config:", clearedConfig);
              onSubmit && onSubmit(clearedConfig, field.name);
            }}
          />
        </Form.Item>
      );
    }
    case "text": {
      return (
        <Form.Item label={cleanLabel(config?.text)} name={[field.name, "answer"]}>
          <Input
            type="text"
            placeholder={config?.optional ? "facultatif" : undefined}
            onChange={(e) => handleThrottleSearch(e.target.value)}
          />
        </Form.Item>
      );
    }
    case "radio": {
      const options = config.answers
        ?.filter((item: string) => Boolean(item))
        ?.map((item: string) => {
          return {
            value: item,
            label: item,
          };
        });

      return (
        <Form.Item label={cleanLabel(config?.text)} name={[field.name, "answer"]}>
          <Radio.Group
            options={options}
            onChange={(e) => {
              onSubmit &&
                onSubmit({ ...config, answer: e.target.value }, field.name);
            }}
          />
        </Form.Item>
      );
    }
    default:
      return null;
  }
};

export default PanelForm;
